<template>
  <div class="container-page">
    <p class="title-inner">Бюджетная политика</p>
    <div class="mb-16">
      <ButtonComponent
        v-permission="permissions.import"
        :disabled="importInProgress"
        @click="importExcel()"
        mod="gradient-bg"
      >
        {{ !importInProgress ? 'Загрузить из Excel' : 'Загрузка...' }}
      </ButtonComponent>
    </div>
    <div class="mb-4">
      <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
    </div>
    <div v-if="isAccessAllowed">
      <div class="table-container mb-16 js-table-arrow">
        <table class="stockTable">
          <thead>
            <tr>
              <th>Страна</th>
              <th>Государственные доходы, % ВВП</th>
              <th>Государственный долг, % ВВП</th>
              <th>Общий кредит нефинансовому сектору</th>
              <th>Резервы</th>
              <th>Net lending/borrowing, % of GDP</th>
              <th>Год актуализации</th>
            </tr>
            <tr>
              <th class="filter">
                <FilterInput
                  type="select2"
                  :select2Options="countries"
                  :select2Settings="select2Settings"
                  v-model="filter.countryId"
                  placeholder="(Все)"
                  @valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.revenueFrom"
                    class="mr-2"
                    placeholder="от"
                    @valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.revenueTo"
                    placeholder="до"
                    @valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.governmentDebtFrom"
                    class="mr-2"
                    placeholder="от"
                    @valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.governmentDebtTo"
                    placeholder="до"
                    @valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.nonFinancialCreditFrom"
                    class="mr-2"
                    placeholder="от"
                    @valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.nonFinancialCreditTo"
                    placeholder="до"
                    @valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.reservesFrom"
                    class="mr-2"
                    placeholder="от"
                    @valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.reservesTo"
                    placeholder="до"
                    @valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.netLendingBorrowingFrom"
                    class="mr-2"
                    placeholder="от"
                    @valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.netLendingBorrowingTo"
                    placeholder="до"
                    @valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th>
                <FilterInput
                  type="number"
                  v-model="filter.year"
                  placeholder="год"
                  @valueChangedDebounced="changeFilter()"
                />
              </th>
            </tr>
          </thead>
          <tbody v-if="page.items?.length && !isLoading">
            <tr v-for="item in page.items" :key="item.id">
              <td>{{ item.country.nameFull || item.country.name }}</td>
              <td>{{ item.revenue }}</td>
              <td>{{ item.governmentDebt }}</td>
              <td>{{ item.nonFinancialCredit }}</td>
              <td>{{ item.reserves }}</td>
              <td>{{ item.netLendingBorrowing }}</td>
              <td>{{ item.year }}</td>
            </tr>
          </tbody>
          <tbody v-else-if="isLoading">
            <tr>
              <td class="text-center" colspan="7">Загрузка...</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="7">Нет данных.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="page.pagesTotal > 1">
        <Pagination
          :pageCount="page.pagesTotal"
          :currentPage="page.pageNumber"
          @change="onPageChange($event, false)"
          @more="onPageChange($event, true)"
        />
      </div>
    </div>
    <section>
      <ModalComponent v-model="importModal" title="Импорт файла" @close="closeModal">
        <div class="form-group">
          <label class="form-label" for="budget-import-year">Год актуализации данных</label>
          <ComponentInput id="budget-import-year" v-model="importYear" type="number" />
          <div class="invalid-msg" v-if="importYearInvalid">Поле обязательно для заполнения</div>
        </div>
        <ImportModal @doimport="doImport" @close="closeModal" />
      </ModalComponent>
    </section>
  </div>
</template>

<script>
  import ComponentInput from '@/common/components/ComponentInput.vue';
  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import ImportModal from '@/components/modals/ImportModal.vue';

  import FilterInput from '../../../common/components/FilterInput.vue';
  import ModalComponent from '../../../components/modals/ModalComponent.vue';
  import Api from '../api/index';

  const PERMISSIONS = {
    view: Constants.Permissions.BudgetPolicyView,
    import: Constants.Permissions.BudgetPolicyImport,
  };

  export default {
    name: 'BudgetPolicy',
    components: { FilterInput, ImportModal, ModalComponent, ButtonComponent, DefaultFilter, ComponentInput },
    mixins: [security],
    data() {
      return {
        route: 'budgetpolicy',
        page: {},
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        filter: {
          countryId: null,
          revenueFrom: null,
          revenueTo: null,
          governmentDebtFrom: null,
          governmentDebtTo: null,
          nonFinancialCreditFrom: null,
          nonFinancialCreditTo: null,
          reservesFrom: null,
          reservesTo: null,
          netLendingBorrowingFrom: null,
          netLendingBorrowingTo: null,
          year: null,
        },
        countries: [],
        select2Settings: Constants.select2Settings,
        importInProgress: false,
        importModal: false,
        importYear: null,
        importYearInvalid: false,
        isLoading: false,
      };
    },
    computed: {
      permissions: () => PERMISSIONS,
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then((isAllowed) => {
        if (!isAllowed) return;

        loadFilter(this.$options.name).then((response) => {
          let filter = response;
          if (filter) {
            this.filter = filter;
          }
          this.loadDictionaries();
          this.loadPage();
        });
      });
    },
    methods: {
      loadPage(append) {
        this.isLoading = !append;
        const request = Object.assign(this.filter, this.request);
        Api.search(this.route, request)
          .then((response) => {
            if (!append) {
              this.page = {};
              this.$nextTick(() => {
                this.page = response.data;
              });
            } else if (response.data) {
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      loadDictionaries() {
        Utils.searchNsi('countrylistwithdata', {})
          .then((response) => {
            this.countries = response.data.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
      },
      importExcel() {
        this.importModal = true;
      },
      doImport(file) {
        if (!this.importYear || this.importYear * 1 < 2000 || this.importYear * 1 > 2100) {
          this.importYearInvalid = true;
          return;
        }
        this.importYearInvalid = false;

        this.closeModal();
        this.importInProgress = true;
        Api.downloadFile(this.route + '/' + this.importYear, file)
          .then(() => {
            Constants.alert.fire('Импорт', 'Импорт успешно выполнен.', 'success');
            this.loadPage();
            this.importInProgress = false;
          })
          .catch((error) => {
            console.log(error?.response?.data);
            Constants.alert.fire(
              'Импорт',
              error?.response?.data ? error?.response?.data : 'Ошибка при импорте данных.',
              'error',
            );
            this.importInProgress = false;
          });
      },
      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      closeModal() {
        this.importModal = false;
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
    },
  };
</script>

<style lang="scss">
  .filter {
    .range {
      display: flex;
      .input-group-wrapper {
        input {
          padding: 4px 8px;
          min-width: 80px;
          max-width: 80px;
        }
      }
    }
  }
</style>
